import { useQueryClient } from '@tanstack/react-query';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import { useOptionalChatContext } from '../../contexts/ChatContextProvider';
import { queryKeys } from '../../lib/queryClient';
import { useProject } from '../data/useProjects';
import { useEntityAccess } from '../data/useEntityAccess';
import type { Artifact } from '../data/useArtifacts';
import {
  workspaceDestination,
  type CreateDestination,
} from '../../components/features/create/destination/createDestination';

/**
 * The collection the page in view registers with the chat context, if any:
 * a collection page, or an artifact page whose artifact (read from the query
 * cache, never fetched) sits in a collection. An attachment inside a note
 * walks up one level to the note's collection.
 */
function useCollectionInView(): string | null {
  const context = useOptionalChatContext();
  const queryClient = useQueryClient();
  if (!context) return null;
  if (context.type === 'project') return context.id;
  let artifact = queryClient.getQueryData<Artifact>(queryKeys.artifacts.detail(context.id));
  if (artifact?.parentEntityType.startsWith('artifact.')) {
    artifact = queryClient.getQueryData<Artifact>(
      queryKeys.artifacts.detail(artifact.parentEntityId),
    );
  }
  return artifact?.parentEntityType === 'project' ? artifact.parentEntityId : null;
}

export interface DefaultCreateDestination {
  /** `null` only while the workspace itself is unknown (still loading, or there is none). */
  readonly destination: CreateDestination | null;
  /** The destination is the collection the page shows ("From current view"). */
  readonly fromView: boolean;
  /** False while the workspace, the collection in view, or whether it can be added to, is loading. */
  readonly ready: boolean;
  /** The collection in view, when the user can only view it (so it is not the default). */
  readonly viewOnlyName?: string;
}

/**
 * Where the create modal starts: the collection in view when the user can add
 * to it, else the workspace root. Only looks anything up while `enabled` (the
 * modal has a request), so a closed modal costs no requests.
 */
export function useDefaultCreateDestination(enabled: boolean): DefaultCreateDestination {
  const { workspace, isLoading: workspaceLoading } = useWorkspace();
  const inView = useCollectionInView();
  const collectionId = enabled ? (inView ?? undefined) : undefined;
  const project = useProject(collectionId);
  const access = useEntityAccess(collectionId ? 'PROJECT' : undefined, collectionId);
  // Not ready while it loads: "ready" with no destination means there is no
  // workspace, and the modal drops the request (a Create clicked during page
  // boot used to vanish that way).
  if (!workspace) return { destination: null, fromView: false, ready: !workspaceLoading };
  const root = workspaceDestination(workspace);
  if (!collectionId) return { destination: root, fromView: false, ready: true };
  if (project.isLoading || access.isLoading) {
    return { destination: root, fromView: false, ready: false };
  }
  if (!project.data) return { destination: root, fromView: false, ready: true };
  const canEdit = access.data?.permission === 'EDIT' || access.data?.permission === 'MANAGE';
  if (!canEdit) {
    return { destination: root, fromView: false, ready: true, viewOnlyName: project.data.name };
  }
  return {
    destination: { type: 'project', id: collectionId, name: project.data.name },
    fromView: true,
    ready: true,
  };
}
